import { map, Observable } from 'rxjs';
import { inject, Pipe, PipeTransform } from '@angular/core';

/**
 * services
 */
import { ILayoutLang, LangService, LayoutLang } from '@core/services';

/**
 * utilities
 */
import { INTL_LOCALES } from '@core/utilities';

/** Currency every amount in the app is denominated in. */
const APP_CURRENCY_CODE = 'SAR';

/**
 * Pipe that formats money in the currently active language.
 *
 * **Logic:** same shape as `LocalizedNumberPipe` — subscribes to `LangService.changeLang$`
 * and maps each emission to the amount formatted for that language's locale, so a runtime
 * language switch re-renders the value.
 *
 * **Why not `CurrencyPipe`:** it resolves its locale from `LOCALE_ID`, which is bound once at
 * bootstrap, so under `@ngx-translate` it would keep formatting in the language the app
 * started in. It cannot be subclassed into this shape either — it declares `transform` as
 * returning `string | null`, which an `Observable` override does not satisfy.
 *
 * **Why `Intl` and not `formatCurrency`:** it is the rule across the app now, not this pipe's
 * exception — `LocalizedNumberPipe` and the energy-source chart formatter resolve the same
 * {@link INTL_LOCALES} entry, so money and the readings beside it group and punctuate
 * identically. Angular's formatter joins a digit array, so it
 * emits ASCII digits for every locale and leaves Arabic-Indic numerals to a second pass.
 * `Intl` applies the locale's numbering system itself — `٧٬٠٨٣٫٢٦ ر.س.\u200F` — which is why no
 * `toArabicNumerals` call is needed here. It is also what Angular's own deprecation notice on
 * `getCurrencySymbol` points to.
 *
 * The pipe is **pure** and exposes an `Observable`, so it is used with the `async` pipe.
 */
@Pipe({
  name: 'localizedCurrency',
})
export class LocalizedCurrencyPipe implements PipeTransform {

  /** LangService for current language. */
  public readonly _langService = inject(LangService);

  /**
   * Transforms an amount into currency text for the current language.
   *
   * @param value - Amount to format. `null`/`undefined` and non-numeric input yield an empty
   * string.
   * @param currencyCode - ISO 4217 code driving the symbol. Defaults to `SAR`.
   * @param currencyDisplay - How to render the currency, as in `Intl.NumberFormat`.
   * @param fractionDigits - Fixed number of decimals. Defaults to `2`.
   * @returns Observable that emits the localized amount whenever the language changes.
   */
  public transform(
    value: number | string | null | undefined,
    currencyCode: string = APP_CURRENCY_CODE,
    currencyDisplay: 'symbol' | 'narrowSymbol' | 'code' | 'name' = 'symbol',
    fractionDigits: number = 2,
  ): Observable<string> {
    return this._langService.changeLang$.pipe(
      map(lang => formatLocalizedCurrency(value, lang, currencyCode, currencyDisplay, fractionDigits)),
    );
  }

}

/**
 * Formats an amount as currency text for one language.
 *
 * @param value - Amount to format. `null`/`undefined` and non-numeric input yield an empty
 * string.
 * @param lang - Language whose {@link INTL_LOCALES} entry drives the format.
 * @param currencyCode - ISO 4217 code driving the symbol. Defaults to `SAR`.
 * @param currencyDisplay - How to render the currency, as in `Intl.NumberFormat`.
 * @param fractionDigits - Fixed number of decimals. Defaults to `2`.
 * @returns The formatted amount.
 *
 * @remarks
 * The formatting body of {@link LocalizedCurrencyPipe}, lifted out so a caller with no
 * template — a file export, say — renders an amount exactly as the screen does rather than
 * reimplementing the symbol placement beside it. The pipe is the reactive wrapper around
 * this function; templates still use the pipe.
 */
export function formatLocalizedCurrency(
  value: number | string | null | undefined,
  lang: ILayoutLang,
  currencyCode: string = APP_CURRENCY_CODE,
  currencyDisplay: 'symbol' | 'narrowSymbol' | 'code' | 'name' = 'symbol',
  fractionDigits: number = 2,
): string {
  if (value == null || value === '') return '';

  const amount = Number(value);

  if (!Number.isFinite(amount)) return '';

  const parts = new Intl.NumberFormat(INTL_LOCALES[lang] ?? INTL_LOCALES[LayoutLang.En], {
    style: 'currency',
    currency: currencyCode,
    currencyDisplay,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).formatToParts(amount);

  const currencyIndex = parts.findIndex(part => part.type === 'currency');
  const amountIndex = parts.findIndex(part => part.type === 'integer');

  // Arabic already trails the currency (`\u200F٧٬٠٨٣٫٢٦ ر.س.\u200F`), so its parts are joined
  // untouched — including the directional marks the locale emits around them.
  if (currencyIndex === -1 || currencyIndex > amountIndex) {
    return parts.map(part => part.value).join('');
  }

  // English leads with it and reads as `SAR 7,083.26`; moving it to the end gives
  // `7,083.26 SAR`, the value-then-unit order every other cell uses (kWh, A, %).
  const symbol = parts[currencyIndex].value;
  const amountText = parts.filter((_, index) => index !== currencyIndex).map(part => part.value).join('').trim();

  return `${amountText} ${symbol}`;
}
